import {cva} from 'class-variance-authority'

// "Tactile" shadows for the gradient contained buttons: a gentle colored drop
// shadow + the inner bevel, per Figma (node 24:1431 / 24:1485). The old outer
// "glow" layers were removed — this is just a soft lift plus the pressed-edge bevel.
const TACTILE_PRIMARY =
  'shadow-[1px_2px_5px_0px_#0088951A,inset_1px_1px_1px_0px_#00BACC,inset_-1px_-1px_1px_1px_#008895]'
const TACTILE_CTA =
  'shadow-[1px_2px_5px_0px_#0D503D1A,inset_1px_1px_1px_0px_#7DE9CB,inset_-1px_-1px_1px_1px_#2ADBAA]'

// Pressed state for contained buttons: the Figma `tactile/button/pressed` inner
// shadows (neutral "pushed-in" depth — the two color-specific inner glows are
// omitted so it reads correctly across all contained colors). Pairs with the
// `active:brightness-95` darken in the base.
const PRESSED_CONTAINED =
  'active:shadow-[inset_0px_2px_4px_0px_#00256640,inset_0px_4px_8px_0px_#0025661A]'

const DISABLED_CONTAINED =
  'disabled:bg-none disabled:bg-action-disabled-background disabled:text-action-disabled disabled:shadow-none'
const DISABLED_OUTLINED =
  'disabled:text-action-disabled disabled:border-action-disabled-background disabled:bg-transparent'
const DISABLED_TEXT = 'disabled:text-action-disabled disabled:bg-transparent'

/**
 * Button (v3, MUI-aligned two axes): `variant` (contained/outlined/text) × `color`
 * (primary/secondary/error/cta/inverse) × `size` (small/medium/large).
 *
 * Per Figma, **`cta` is contained-only** — outlined/text are defined for
 * primary/secondary/error/inverse. The `Button` prop type enforces this.
 *
 * Contained primary/cta use the bespoke gradient + tactile shadow from Figma;
 * other contained colors use a solid fill + elevation. Outlined/text are
 * transparent with a tinted hover. Every variant darkens on press
 * (`active:brightness-95`); contained also shows the pressed inset shadow.
 */
export const ButtonVariantsFactory = cva(
  'relative inline-flex shrink-0 select-none items-center justify-center overflow-hidden rounded-pill font-sans font-bold whitespace-nowrap transition-all active:brightness-95 disabled:cursor-not-allowed disabled:pointer-events-none disabled:brightness-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        contained: '',
        outlined: 'border bg-transparent',
        text: 'bg-transparent',
      },
      color: {
        primary: '',
        secondary: '',
        error: '',
        cta: '',
        inverse: '',
      },
      size: {
        small: 'h-8 gap-1.5 px-4 text-[14px]',
        medium: 'h-10 gap-2 px-5 text-[16px]',
        large: 'h-12 gap-2 px-6 text-[18px]',
      },
    },
    compoundVariants: [
      // --- Contained ---
      {
        variant: 'contained',
        color: 'primary',
        class: `text-white bg-gradient-to-b from-[#00bacc] to-[#30d7e8] ${TACTILE_PRIMARY} hover:brightness-105 ${PRESSED_CONTAINED} ${DISABLED_CONTAINED}`,
      },
      {
        variant: 'contained',
        color: 'cta',
        class: `text-white bg-gradient-to-b from-[#2adbaa] to-[#7de9cb] ${TACTILE_CTA} hover:brightness-105 ${PRESSED_CONTAINED} ${DISABLED_CONTAINED}`,
      },
      {
        variant: 'contained',
        color: 'error',
        class: `text-white bg-error shadow-elevation-2 hover:bg-error-dark ${PRESSED_CONTAINED} ${DISABLED_CONTAINED}`,
      },
      {
        variant: 'contained',
        color: 'secondary',
        class: `text-white bg-secondary shadow-elevation-2 hover:bg-secondary-dark ${PRESSED_CONTAINED} ${DISABLED_CONTAINED}`,
      },
      {
        variant: 'contained',
        color: 'inverse',
        // White fill, dark text (text/primary #003035 — NOT the teal brand
        // `primary`), 1px divider outline, no shadow. Per Figma.
        class: `text-text-primary bg-white border border-divider hover:bg-background-subdued ${PRESSED_CONTAINED} disabled:border-transparent ${DISABLED_CONTAINED}`,
      },
      /*
       * --- Outlined (no `cta` — contained-only per Figma) ---
       *
       * The label colour is the variant's *own* token, which for `secondary` is
       * NOT `secondary`: Figma's Button component set gives
       * `Variant=Outlined, Color=Secondary` the label `Secondary/700` (#24575D),
       * while `secondary` alone is secondary/main, #0D1F21 — a near-black that
       * reads as a disabled control next to the #2C686F border.
       *
       * The token to reach for is `text-text-secondary`, NOT `text-secondary-700`:
       * Figma's `Secondary/700` and this repo's `secondary` ColorScale `700`
       * (#328A94) are different colours that happen to share a name. #24575D is
       * carried by the `text.secondary` palette entry.
       *
       * `primary` and `error` do resolve to their own main, which is why only
       * this pair needs saying out loud.
       */
      {
        variant: 'outlined',
        color: 'primary',
        class: `text-primary border-[#00a2b1] hover:bg-primary-faint active:bg-action-hover ${DISABLED_OUTLINED}`,
      },
      {
        variant: 'outlined',
        color: 'secondary',
        // `text-text-secondary`, not `text-secondary`: the Figma labels this
        // button `Secondary/700` (#24575D), while `secondary` alone is
        // secondary/main (#0D1F21) — a near-black that reads as a disabled or
        // mis-themed control next to the #2C686F border. See the note above.
        class: `text-text-secondary border-[#2c686f] hover:bg-secondary-faint active:bg-action-hover ${DISABLED_OUTLINED}`,
      },
      {
        variant: 'outlined',
        color: 'error',
        class: `text-error border-error hover:bg-error-faint active:bg-error-faint ${DISABLED_OUTLINED}`,
      },
      {
        variant: 'outlined',
        color: 'inverse',
        class: `text-white border-white hover:bg-white/10 active:bg-white/20 ${DISABLED_OUTLINED}`,
      },
      // --- Text (no `cta` — contained-only per Figma) ---
      {
        variant: 'text',
        color: 'primary',
        class: `text-primary hover:bg-primary-faint active:bg-action-hover ${DISABLED_TEXT}`,
      },
      {
        variant: 'text',
        color: 'secondary',
        // Same `Secondary/700` label as its outlined sibling — the Figma gives
        // the two variants one text colour and differs only in the border.
        class: `text-text-secondary hover:bg-secondary-faint active:bg-action-hover ${DISABLED_TEXT}`,
      },
      {
        variant: 'text',
        color: 'error',
        class: `text-error hover:bg-error-faint active:bg-error-faint ${DISABLED_TEXT}`,
      },
      {
        variant: 'text',
        color: 'inverse',
        class: `text-white hover:bg-white/10 active:bg-white/20 ${DISABLED_TEXT}`,
      },
    ],
    defaultVariants: {
      variant: 'contained',
      color: 'primary',
      size: 'medium',
    },
  },
)
